import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import { VehicleFilterParameters } from '../utils/vehicle-filter-types';
import BevManagerFilterContext, { BevManagerFilterCache } from './bev-manager-filter-context';

interface CachedFilters {
  readonly solutionId: string;
  readonly filters: VehicleFilterParameters;
}

const BevManagerFilterProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const { pathname } = useLocation();
  const solutionId = matchPath('/solution/:solutionId/*', pathname)?.params.solutionId;
  const cachedFiltersRef = useRef<CachedFilters | null>(null);
  const activeSolutionIdRef = useRef(solutionId);

  useEffect(() => {
    if (!solutionId || activeSolutionIdRef.current === solutionId) {
      return;
    }

    if (cachedFiltersRef.current?.solutionId !== solutionId) {
      cachedFiltersRef.current = null;
    }

    activeSolutionIdRef.current = solutionId;
  }, [solutionId]);

  const getFilters = useCallback((solutionId: string): VehicleFilterParameters | null => {
    const cachedFilters = cachedFiltersRef.current;

    return cachedFilters?.solutionId === solutionId ? cachedFilters.filters : null;
  }, []);

  const setFilters = useCallback((solutionId: string, filters: VehicleFilterParameters): void => {
    cachedFiltersRef.current = { solutionId, filters };
  }, []);

  const clearFilters = useCallback((): void => {
    cachedFiltersRef.current = null;
  }, []);

  const value = useMemo<BevManagerFilterCache>(
    () => ({ getFilters, setFilters, clearFilters }),
    [clearFilters, getFilters, setFilters],
  );

  return <BevManagerFilterContext value={value}>{children}</BevManagerFilterContext>;
};

export default BevManagerFilterProvider;
